export interface CaptureMetadata {
  episodeId: string;
  sceneId: string;
  shotId: string;
  time: number;
  camera: {
    position: [number, number, number];
    target: [number, number, number];
    fov: number;
  };
}

export class CaptureSystem {
  private recorder?: MediaRecorder;
  private chunks: Blob[] = [];

  constructor(private readonly canvas: HTMLCanvasElement) {}

  async captureStill(metadata: CaptureMetadata): Promise<void> {
    const blob = await new Promise<Blob>((resolve, reject) => {
      this.canvas.toBlob((result) => (result ? resolve(result) : reject(new Error('Canvas capture failed.'))), 'image/png');
    });
    this.download(blob, `${metadata.episodeId}-${metadata.sceneId}-${metadata.shotId}-${metadata.time.toFixed(2)}.png`);
  }

  exportDirection(metadata: CaptureMetadata, episode: unknown): void {
    const payload = JSON.stringify(
      {
        format: 'ranger-pups-studio.direction.v1',
        capturedAt: new Date().toISOString(),
        metadata,
        episode,
      },
      null,
      2,
    );
    this.download(new Blob([payload], { type: 'application/json' }), `${metadata.episodeId}-direction.json`);
  }

  startRecording(onStopped?: (blob: Blob) => void): boolean {
    if (this.recorder?.state === 'recording' || typeof MediaRecorder === 'undefined') return false;
    const stream = this.canvas.captureStream(30);
    const mimeType = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'].find((type) =>
      MediaRecorder.isTypeSupported(type),
    );
    this.chunks = [];
    this.recorder = new MediaRecorder(stream, mimeType ? { mimeType, videoBitsPerSecond: 8_000_000 } : undefined);
    this.recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    });
    this.recorder.addEventListener('stop', () => {
      const blob = new Blob(this.chunks, { type: this.recorder?.mimeType || 'video/webm' });
      this.download(blob, `ranger-pups-take-${new Date().toISOString().replaceAll(':', '-')}.webm`);
      onStopped?.(blob);
      for (const track of stream.getTracks()) track.stop();
    });
    this.recorder.start(250);
    return true;
  }

  stopRecording(): boolean {
    if (this.recorder?.state !== 'recording') return false;
    this.recorder.stop();
    return true;
  }

  get isRecording(): boolean {
    return this.recorder?.state === 'recording';
  }

  private download(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 2_000);
  }
}

